import { actionFilter, wageFilter } from "@/utils/DigitalAccount";
import { Copy, Flex, Page, Table, Tooltip, Typography, useMessagedAsyncRetry } from "@payping/web-components";
import { CuToman, LineChart } from "@payping/icons";

import { useAppDispatch, useAppSelector, useAppTranslation } from "@/utils";
import { useLocation, useNavigate } from "react-router-dom";
import { SubLayout } from "@/components/SubLayout";
import { AccountTurnOverFilter } from "@/components/DashDigitalAccount/AccountTurnOverFilter";
import { submitQueryParams } from "@payping/utils/query-string";
import type { ColumnsType } from "@payping/web-components/components/table/types";
import { DepositStatementActionEnum } from "@payping/api";
import type { DepositStatement } from "@payping/api/app/api/digitalAccountSamanTypes";
import { getDepositStatements } from "@payping/redux/actions";
import qs from "query-string";
import { dateFormats, jalaliNow, toJalali } from "@payping/utils/dateUtils";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";
import { ListNumberQueryParamsType, ListStringQueryParamsType } from "./types";

const TurnOver = () => {
  const { t } = useAppTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const parsedSearch = qs.parse(location.search);

  const { depositStatements } = useAppSelector((state) => state.digitalAccount.saman);
  const { startDate, endDate, referenceNumber }: ListStringQueryParamsType = qs.parse(location.search);
  const {
    turnOverType,
    pageNumber = 1,
    pageSize = 10,
  }: ListNumberQueryParamsType = qs.parse(location.search, {
    parseNumbers: true,
  });

  const { loading, retry } = useMessagedAsyncRetry(
    async () => {
      const fromDate = startDate
        ? toJalali({
            date: startDate,
            dateFormat: dateFormats.standard.utcDateTime,
            toFormat: dateFormats.ui.jalaliDate,
          }).object
        : jalaliNow({
            manipulate: [{ args: [1, "month"], method: "subtract" }],
            toFormat: dateFormats.standard.utcDateTime,
          }).object.startOf("day");

      const toDate = endDate
        ? toJalali({
            date: endDate,
            dateFormat: dateFormats.standard.utcDateTime,
            toFormat: dateFormats.ui.jalaliDate,
          }).object
        : jalaliNow({
            toFormat: dateFormats.standard.utcDateTime,
          }).object;
      await dispatch(
        getDepositStatements({
          action: actionFilter(turnOverType),
          pageSize,
          pageNumber,
          startDate: fromDate?.toDate(),
          endDate: toDate?.toDate(),
          referenceNumber,
          debitType: wageFilter(turnOverType),
        }),
      );
    },
    [dispatch, turnOverType, pageSize, pageNumber, startDate, endDate, referenceNumber],
    { success: false, useProgress: true },
  );

  const dataTable = depositStatements?.statements?.map((item, index) => ({ ...item, key: index }));

  const cols: ColumnsType<DepositStatement> = [
    {
      dataIndex: "action",
      render(_, tableData) {
        return (
          <Flex gap={16}>
            <LineChart
              key={1}
              color={tableData.action === DepositStatementActionEnum.CREDIT ? "green" : "red"}
              width={16}
            />
            <Typography.Text>
              {tableData.action === DepositStatementActionEnum.DEBIT
                ? t("digitalAccount.yourAccount.debit")
                : t("digitalAccount.yourAccount.credit")}
            </Typography.Text>
          </Flex>
        );
      },
      title: t("digitalAccount.samanTurnOver.table.type"),
    },
    {
      dataIndex: "date",
      render(_, tableData) {
        return (
          <div className={"tw:flex! tw:justify-start!"}>
            <div style={{ direction: "ltr" }}>
              {
                toJalali({
                  date: tableData.date,
                  dateFormat: dateFormats.standard.utcDateTime,
                  toFormat: dateFormats.ui.jalaliDateTime,
                }).string
              }
            </div>
          </div>
        );
      },
      title: t("digitalAccount.samanTurnOver.table.date"),
    },
    {
      className: "en-font",
      dataIndex: "referenceNumber",
      render(_, tableData) {
        return <Copy text={tableData.referenceNumber || ""}>{tableData.referenceNumber}</Copy>;
      },
      title: t("digitalAccount.samanTurnOver.table.referenceNumber"),
    },
    {
      dataIndex: "description",
      ellipsis: true,
      render(_, tableData) {
        return (
          <Typography.Text ellipsis>
            <Tooltip title={tableData.description}>{tableData.description}</Tooltip>
          </Typography.Text>
        );
      },
      title: t("digitalAccount.samanTurnOver.table.description"),
    },
    {
      dataIndex: "transferAmount",
      render(_, tableData) {
        return (
          <Flex align="center">
            <div>{`${formatLocalizedNumber({ value: Math.trunc((tableData.transferAmount || 0) / 10) })}`}</div>
            <CuToman width={24} height={24} />
          </Flex>
        );
      },
      title: t("digitalAccount.samanTurnOver.table.transferAmount"),
    },
  ];

  const totalRecord = (pageNumber - 1) * pageSize + (dataTable?.length || 0) + 1;

  return (
    <Page>
      <SubLayout>
        <Flex vertical gap={16}>
          <AccountTurnOverFilter loading={loading} retry={retry} />
          <Table
            loading={loading}
            hasIdColumn
            dataSource={dataTable}
            columns={cols}
            pagination={{
              onChange: (page, newSize): void => {
                const newValues = {
                  ...parsedSearch,
                  pageNumber: page,
                  pageSize: newSize,
                };
                submitQueryParams({
                  navigate,
                  newValues,
                  parsedSearch,
                  pathname: location.pathname,
                  retry,
                });
              },
              onShowSizeChange: (_currentPage, newSize): void => {
                const newValues = {
                  ...parsedSearch,
                  pageNumber: 1,
                  pageSize: newSize,
                };
                submitQueryParams({
                  navigate,
                  newValues,
                  parsedSearch,
                  pathname: location.pathname,
                  retry,
                });
              },
              pageNumber,
              pageSize,
              showTotal: (total, range): string =>
                t("report.table.showTotal", { range: `${range[0]}-${range[1]}`, total }),
              total: totalRecord,
            }}
          />
        </Flex>
      </SubLayout>
    </Page>
  );
};

export default TurnOver;
